import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Mail } from "lucide-react";
import { OgmaLogo } from "@/components/OgmaLogo";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { ErrorAlert } from "@/components/ui/ErrorAlert";
import { authApi } from "@/features/auth/api";
import { extractMessage } from "@/lib/errors";

export function ForgotPasswordPage() {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      await authApi.requestPasswordReset(email);
      setSent(true);
    } catch (err) {
      setError(extractMessage(err));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="flex h-screen w-screen items-center justify-center bg-muted/40">
      <div className="flex w-80 flex-col items-center gap-6 rounded-xl border border-border bg-card px-10 py-12 text-center shadow-sm">
        <OgmaLogo size={40} />
        {sent ? (
          <>
            <Mail className="size-8 text-primary" />
            <div className="flex flex-col gap-1">
              <h1 className="text-lg font-semibold text-foreground">
                {t("passwordReset.emailSentTitle")}
              </h1>
              <p className="text-sm text-muted-foreground">
                {t("passwordReset.emailSentDescription")}
              </p>
            </div>
          </>
        ) : (
          <>
            <div className="flex flex-col gap-1">
              <h1 className="text-lg font-semibold text-foreground">
                {t("passwordReset.forgotTitle")}
              </h1>
              <p className="text-sm text-muted-foreground">
                {t("passwordReset.forgotDescription")}
              </p>
            </div>
            <form onSubmit={handleSubmit} className="flex w-full flex-col gap-3 text-left">
              <Input
                type="email"
                placeholder={t("auth.emailPlaceholder")}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="username"
                autoFocus
              />
              {error && <ErrorAlert message={error} />}
              <Button type="submit" disabled={loading} className="w-full">
                {loading ? t("passwordReset.submitting") : t("passwordReset.submit")}
              </Button>
            </form>
          </>
        )}
        <Link
          to="/login"
          className="text-sm text-muted-foreground underline-offset-2 hover:underline"
        >
          {t("passwordReset.backToLogin")}
        </Link>
      </div>
    </div>
  );
}
